// ---------------------------------------------------------------------------
// C1: Schema.org Auto-Generation from Puck Components
// ---------------------------------------------------------------------------
// Auto-detects page type from components and generates JSON-LD.
// FAQ accordion → FAQPage. Pricing table → Product. LocalBusiness from KB.
// No manual config required — schema is derived from what's on the page.
// ---------------------------------------------------------------------------

interface PuckComponent { type: string; props: Record<string, unknown>; }
interface KbBusinessInfo { name?: string; address?: string; phone?: string; hours?: string; description?: string; logo?: string; }

export interface SchemaOrgOutput {
  schemas: Array<Record<string, unknown>>;
  jsonLd: string; // Ready to inject into <head>
}

function normalizeType(type: string): string {
  return type
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/[\s_]+/g, "-")
    .toLowerCase();
}

function asString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function toComponentKind(component: PuckComponent): string {
  return normalizeType(component.type);
}

function readPrimaryText(component: PuckComponent): string {
  const candidates = [
    component.props.title,
    component.props.heading,
    component.props.name,
    component.props.label,
    component.props.text,
    component.props.content,
  ];

  for (const candidate of candidates) {
    const resolved = asString(candidate);
    if (resolved) {
      return resolved;
    }
  }

  return "";
}

function looksLikeServiceContext(label: string, pageUrl: string): boolean {
  const haystack = `${label} ${pageUrl}`.toLowerCase();
  return /service|services|treatment|treatments|offering|offerings|package|packages|therapy|consultation|care/.test(haystack);
}

function looksLikeEventContext(label: string): boolean {
  return /webinar|workshop|masterclass|event|session|class|training|summit|live/.test(label.toLowerCase());
}

function resolveEventName(components: PuckComponent[]): string {
  for (const component of components) {
    const value = readPrimaryText(component);
    if (value) {
      return value;
    }
  }
  return "Upcoming event";
}

function resolveWebinarAction(component: PuckComponent): boolean {
  const candidates = [component.props.action, component.props.primaryAction];
  return candidates.some((candidate) => {
    if (!isRecord(candidate)) {
      return false;
    }
    const kind = asString(candidate.kind);
    return kind === "register_webinar" || kind === "webinar_registration";
  });
}

/**
 * Generate Schema.org JSON-LD from page components + optional business info from KB.
 * Auto-detects: FAQPage, Product, LocalBusiness, Event, Course, HowTo.
 */
export function generateSchemaOrg(
  components: PuckComponent[],
  pageUrl: string,
  businessInfo?: KbBusinessInfo,
): SchemaOrgOutput {
  const normalizedComponents = components.map((component) => ({
    ...component,
    type: toComponentKind(component),
  }));
  const schemas: Array<Record<string, unknown>> = [];

  // FAQPage — detected from FAQ/accordion components
  const faqComponents = normalizedComponents.filter(c =>
    c.type === "faq" || c.type === "accordion" || c.type === "faq-accordion",
  );
  if (faqComponents.length > 0) {
    const questions: Array<{ question: string; answer: string }> = [];
    for (const faq of faqComponents) {
      const items = (faq.props.items ?? faq.props.questions ?? []) as Array<{ question?: string; answer?: string; title?: string; content?: string }>;
      for (const item of items) {
        const q = item.question ?? item.title ?? "";
        const a = item.answer ?? item.content ?? "";
        if (q && a) questions.push({ question: q, answer: a });
      }
    }
    if (questions.length > 0) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: questions.map(q => ({
          "@type": "Question",
          name: q.question,
          acceptedAnswer: { "@type": "Answer", text: q.answer },
        })),
      });
    }
  }

  // Product — detected from pricing components
  const pricingComponents = normalizedComponents.filter(c =>
    c.type === "pricing" || c.type === "pricing-table" || c.type === "price-card",
  );
  for (const pricing of pricingComponents) {
    const plans = (pricing.props.plans ?? pricing.props.items ?? []) as Array<{ name?: string; price?: string | number; description?: string; currency?: string }>;
    for (const plan of plans) {
      if (plan.name && plan.price) {
        const priceNum = typeof plan.price === "string" ? parseFloat(plan.price.replace(/[^0-9.]/g, "")) : plan.price;
        if (!isNaN(priceNum)) {
          schemas.push({
            "@context": "https://schema.org",
            "@type": "Product",
            name: plan.name,
            description: plan.description ?? plan.name,
            offers: {
              "@type": "Offer",
              price: priceNum,
              priceCurrency: plan.currency ?? "EUR",
              url: pageUrl,
            },
          });
        }
      }
    }
  }

  // Service — detected from service-list style components when the surrounding page
  // clearly represents services or treatments, not generic benefits.
  const serviceComponents = normalizedComponents.filter((component) =>
    component.type === "feature-grid" || component.type === "calendar-embed" || component.type === "booking-widget",
  );
  for (const component of serviceComponents) {
    const sectionHeading = readPrimaryText(component);

    if (component.type === "feature-grid") {
      const featureItems = Array.isArray(component.props.features)
        ? component.props.features
        : [];

      if (!looksLikeServiceContext(sectionHeading, pageUrl) && !looksLikeServiceContext(resolveEventName(normalizedComponents), pageUrl)) {
        continue;
      }

      for (const item of featureItems) {
        if (!isRecord(item)) {
          continue;
        }
        const name = asString(item.heading ?? item.title ?? item.name);
        const description = asString(item.description ?? item.text);
        if (!name) {
          continue;
        }

        schemas.push({
          "@context": "https://schema.org",
          "@type": "Service",
          name,
          description: description || undefined,
          url: pageUrl || undefined,
        });
      }
      continue;
    }

    if (sectionHeading && looksLikeServiceContext(sectionHeading, pageUrl)) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Service",
        name: sectionHeading,
        url: pageUrl || undefined,
      });
    }
  }

  // Review — detected from testimonial grids using real customer quotes.
  const testimonialComponents = normalizedComponents.filter((component) =>
    component.type === "testimonial-grid",
  );
  for (const component of testimonialComponents) {
    const testimonials = Array.isArray(component.props.testimonials)
      ? component.props.testimonials
      : [];

    for (const item of testimonials) {
      if (!isRecord(item)) {
        continue;
      }

      const reviewBody = asString(item.quote ?? item.text);
      const authorName = asString(item.name ?? item.author);
      if (!reviewBody || !authorName) {
        continue;
      }

      const ratingValue = Number(item.rating ?? 5);
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Review",
        author: {
          "@type": "Person",
          name: authorName,
        },
        reviewBody,
        reviewRating: Number.isFinite(ratingValue)
          ? {
            "@type": "Rating",
            ratingValue: Math.max(1, Math.min(5, Math.round(ratingValue))),
            bestRating: 5,
            worstRating: 1,
          }
          : undefined,
      });
    }
  }

  // Event — detected from webinar/event surfaces or explicit webinar actions.
  const eventComponents = normalizedComponents.filter((component) =>
    component.type === "event"
      || component.type === "event-card"
      || component.type === "countdown-timer"
      || resolveWebinarAction(component),
  );
  for (const event of eventComponents) {
    const eventName = asString(event.props.name ?? event.props.title ?? event.props.heading) || resolveEventName(normalizedComponents);
    const startDate = asString(event.props.startDate ?? event.props.date ?? event.props.deadline);
    if (eventName && (startDate || looksLikeEventContext(eventName))) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Event",
        name: eventName,
        startDate: startDate || undefined,
        location: event.props.location ? { "@type": "Place", name: event.props.location } : undefined,
        description: event.props.description,
        url: pageUrl,
        eventAttendanceMode: resolveWebinarAction(event) || looksLikeEventContext(eventName)
          ? "https://schema.org/OnlineEventAttendanceMode"
          : undefined,
        eventStatus: startDate ? "https://schema.org/EventScheduled" : undefined,
      });
    }
  }

  // Course — detected from course components
  const courseComponents = normalizedComponents.filter(c => c.type === "course" || c.type === "course-card");
  for (const course of courseComponents) {
    if (course.props.name || course.props.title) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Course",
        name: course.props.name ?? course.props.title,
        description: course.props.description,
        provider: businessInfo?.name ? { "@type": "Organization", name: businessInfo.name } : undefined,
      });
    }
  }

  // HowTo — detected from steps/process components
  const howToComponents = normalizedComponents.filter(c =>
    c.type === "steps" || c.type === "process" || c.type === "how-to" || c.type === "timeline",
  );
  for (const howTo of howToComponents) {
    const steps = (howTo.props.steps ?? howTo.props.items ?? []) as Array<{ title?: string; text?: string; description?: string }>;
    if (steps.length > 0) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: howTo.props.title ?? "Step-by-step guide",
        step: steps.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.title ?? `Step ${i + 1}`,
          text: s.text ?? s.description ?? "",
        })),
      });
    }
  }

  // LocalBusiness — from knowledge base business info
  if (businessInfo?.name) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: businessInfo.name,
      description: businessInfo.description,
      address: businessInfo.address,
      telephone: businessInfo.phone,
      openingHours: businessInfo.hours,
      logo: businessInfo.logo,
      url: pageUrl,
    });
  }

  // Generate JSON-LD script tag
  // SECURITY: Escape </script> sequences to prevent XSS breakout from user content
  // (e.g., FAQ answers containing "</script><script>alert(1)</script>")
  const jsonLd = schemas.length > 0
    ? `<script type="application/ld+json">${JSON.stringify(schemas.length === 1 ? schemas[0] : schemas).replace(/<\//g, "<\\/")}</script>`
    : "";

  return { schemas, jsonLd };
}
